const PRIMARY_HUE_FAMILIES = [
  { family: 'red', min: 345, max: 360 },
  { family: 'red', min: 0, max: 15 },
  { family: 'orange', min: 16, max: 45 },
  { family: 'green', min: 75, max: 155 },
  { family: 'teal', min: 156, max: 190 },
  { family: 'blue', min: 191, max: 255 },
  { family: 'purple', min: 256, max: 320 },
];

const SPOTLIGHT_FAMILIES = {
  gold: {
    hueMin: 38,
    hueMax: 48,
    saturationMin: 42,
    saturationMax: 58,
    lightnessMin: 52,
    lightnessMax: 68,
    defaultHsl: [42, 49, 60],
  },
  champagne: {
    hueMin: 38,
    hueMax: 50,
    saturationMin: 30,
    saturationMax: 48,
    lightnessMin: 64,
    lightnessMax: 82,
    defaultHsl: [43, 39, 72],
  },
  roseGold: {
    hueMin: 8,
    hueMax: 24,
    saturationMin: 35,
    saturationMax: 55,
    lightnessMin: 58,
    lightnessMax: 74,
    defaultHsl: [16, 45, 66],
  },
  amber: {
    hueMin: 32,
    hueMax: 44,
    saturationMin: 55,
    saturationMax: 75,
    lightnessMin: 48,
    lightnessMax: 64,
    defaultHsl: [38, 65, 56],
  },
  deepBronze: {
    hueMin: 24,
    hueMax: 36,
    saturationMin: 38,
    saturationMax: 58,
    lightnessMin: 34,
    lightnessMax: 50,
    defaultHsl: [30, 48, 42],
  },
  warmSand: {
    hueMin: 34,
    hueMax: 48,
    saturationMin: 24,
    saturationMax: 42,
    lightnessMin: 68,
    lightnessMax: 84,
    defaultHsl: [40, 32, 76],
  },
};

const PRIMARY_TO_SPOTLIGHT = {
  green: 'gold',
  blue: 'champagne',
  purple: 'roseGold',
  red: 'amber',
  orange: 'deepBronze',
  teal: 'warmSand',
  neutral: 'gold',
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function lerp(min, max, t) {
  return min + (max - min) * t;
}

function parseHex(hex) {
  const normalized = String(hex || '').trim().replace('#', '');
  if (normalized.length === 3) {
    return normalized
      .split('')
      .map((char) => `${char}${char}`)
      .join('');
  }
  if (normalized.length === 6) return normalized;
  return '1D4ED8';
}

function hexToRgb(hex) {
  const value = parseHex(hex);
  const r = Number.parseInt(value.slice(0, 2), 16);
  const g = Number.parseInt(value.slice(2, 4), 16);
  const b = Number.parseInt(value.slice(4, 6), 16);

  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return { r: 29, g: 78, b: 216 };
  }

  return { r, g, b };
}

function rgbToHsl({ r, g, b }) {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const delta = max - min;

  let h = 0;
  const l = (max + min) / 2;
  let s = 0;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));

    switch (max) {
      case rNorm:
        h = 60 * (((gNorm - bNorm) / delta) % 6);
        break;
      case gNorm:
        h = 60 * ((bNorm - rNorm) / delta + 2);
        break;
      default:
        h = 60 * ((rNorm - gNorm) / delta + 4);
        break;
    }
  }

  if (h < 0) h += 360;

  return {
    h: Math.round(h),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hslToRgb(h, s, l) {
  const hue = ((h % 360) + 360) % 360;
  const sat = clamp(s, 0, 100) / 100;
  const light = clamp(l, 0, 100) / 100;

  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = light - c / 2;

  let rPrime = 0;
  let gPrime = 0;
  let bPrime = 0;

  if (hue < 60) {
    rPrime = c;
    gPrime = x;
  } else if (hue < 120) {
    rPrime = x;
    gPrime = c;
  } else if (hue < 180) {
    gPrime = c;
    bPrime = x;
  } else if (hue < 240) {
    gPrime = x;
    bPrime = c;
  } else if (hue < 300) {
    rPrime = x;
    bPrime = c;
  } else {
    rPrime = c;
    bPrime = x;
  }

  return {
    r: Math.round((rPrime + m) * 255),
    g: Math.round((gPrime + m) * 255),
    b: Math.round((bPrime + m) * 255),
  };
}

function rgbToHex({ r, g, b }) {
  const toHex = (value) => clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

function classifyPrimaryHueFamily(hsl) {
  if (hsl.s < 12) return 'neutral';

  const match = PRIMARY_HUE_FAMILIES.find((range) => hsl.h >= range.min && hsl.h <= range.max);
  return match?.family || 'neutral';
}

function computeSpotlightHsl(primaryHsl, familyKey) {
  const family = SPOTLIGHT_FAMILIES[familyKey] || SPOTLIGHT_FAMILIES.gold;
  const [defaultH, defaultS, defaultL] = family.defaultHsl;

  const hueSeed = clamp(primaryHsl.h / 360, 0, 1);
  const satSeed = clamp(primaryHsl.s / 100, 0, 1);
  const lightSeed = clamp(primaryHsl.l / 100, 0, 1);

  const dynamicHue = lerp(family.hueMin, family.hueMax, hueSeed);
  const dynamicSat = lerp(family.saturationMin, family.saturationMax, satSeed);
  const dynamicLight = lerp(family.lightnessMin, family.lightnessMax, lightSeed);

  const h = Math.round(lerp(defaultH, dynamicHue, 0.3));
  const s = Math.round(lerp(defaultS, dynamicSat, 0.35));
  const l = Math.round(lerp(defaultL, dynamicLight, 0.35));

  return [
    clamp(h, family.hueMin, family.hueMax),
    clamp(s, family.saturationMin, family.saturationMax),
    clamp(l, family.lightnessMin, family.lightnessMax),
  ];
}

function getSpotlightAccent(primaryHex) {
  const primaryHsl = rgbToHsl(hexToRgb(primaryHex));
  const primaryFamily = classifyPrimaryHueFamily(primaryHsl);
  const spotlightFamily = PRIMARY_TO_SPOTLIGHT[primaryFamily] || 'gold';
  const spotlightHsl = computeSpotlightHsl(primaryHsl, spotlightFamily);
  const [h, s, l] = spotlightHsl;
  const spotlightHex = rgbToHex(hslToRgb(h, s, l));

  return {
    primaryFamily,
    spotlightFamily,
    spotlightHsl,
    spotlightHex,
  };
}

export { getSpotlightAccent };